import { useEffect, useState, useMemo } from "react";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Eye } from "lucide-react";
import { StatusBadge } from "@/pages/mr/Dashboard";

export default function MRHistory() {
  const [requests, setRequests] = useState([]);
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    api.get("/requests").then(({ data }) => setRequests(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => requests.filter((r) => {
    const ql = q.toLowerCase();
    const okQ = !q ||
      r.doctor_name.toLowerCase().includes(ql) ||
      (r.speciality || "").toLowerCase().includes(ql) ||
      r.display_id?.toLowerCase().includes(ql);
    const okD = !date || r.created_at?.startsWith(date);
    return okQ && okD;
  }), [requests, q, date]);

  return (
    <Shell role="mr">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">History</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Your Certificate Requests</h1>
      </div>

      <Card className="p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by doctor name or request ID…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11"
          />
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 md:w-48" />
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed text-slate-500">No requests found.</Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((r) => (
            <Card key={r.id} className="overflow-hidden">
              <button
                type="button"
                className="aspect-[3451/2401] bg-slate-100 w-full block relative group"
                onClick={() => setPreviewId(r.id)}
              >
                <img src={`${API}/requests/${r.id}/preview`} alt={r.doctor_name} className="w-full h-full object-cover" />
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition grid place-items-center text-white opacity-0 group-hover:opacity-100">
                  <Eye className="w-6 h-6" />
                </span>
              </button>
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono font-semibold text-slate-500">{r.display_id}</span>
                  <StatusBadge status={r.status} label={r.status_label} />
                </div>
                <div className="text-sm font-semibold text-slate-900 truncate">{r.doctor_name}</div>
                <div className="text-xs text-slate-500 truncate">{r.speciality || "-"}</div>
                <div className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Preview dialog (no download for MR) */}
      <Dialog open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Certificate Preview</DialogTitle>
          </DialogHeader>
          {previewId && (
            <img
              src={`${API}/requests/${previewId}/preview?t=${Date.now()}`}
              alt="Certificate preview"
              className="w-full rounded-md border border-slate-200"
            />
          )}
          <p className="text-xs text-slate-400 text-center">Preview only — the admin team handles printing &amp; dispatch.</p>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
